import { Grid } from '@mui/material';
import {
  TextField,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useRef, useState } from 'react';
import HOMA from '../../../../public/Images/Homa.png';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import componentConstants from '../../../core-utils/messages/componentConstants';
import { LeftArrow } from '@HomaHealth/ui-component-library';
import { Button } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import InfoBar from '../../atoms/InfoBar';
import Loader from '../../molecules/Loader';

const RESEND_OTP_SECONDS = 45;
export interface LoginOTPOrganismProps {
  email: string;
  setIsOTP: React.Dispatch<React.SetStateAction<boolean>>;
  handleVerifyOtp: (_otp: string) => void;
  handleResendOtp: () => Promise<void>;

  isOTPError: boolean;
  errorMessage: string;

  loading: boolean;
}

const LoginOTPOrganism: React.FC<LoginOTPOrganismProps> = ({
  setIsOTP,
  email,
  handleVerifyOtp,
  handleResendOtp,
  isOTPError,
  errorMessage,
  loading,
}) => {
  const constants = componentConstants.ORGANISMS.RESET_PASSWORD;
  const maskedEmail = email.replace(/(.{2}).+(@.+)/, '$1****$2');

  const [otp, setOtp] = useState<string>('');
  const [otpError, setOtpError] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(RESEND_OTP_SECONDS);
  const [canResend, setCanResend] = useState<boolean>(false);

  // Use a ref for the timer so setResendTimer never causes stale closure issues
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (canResend || resendTimer === 0) {
      if (resendTimer === 0) setCanResend(true);
      return;
    }

    timerRef.current = setTimeout(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resendTimer, canResend]);

  useEffect(() => {
    if (isOTPError) {
      setOtpError(true);
    }
  }, [isOTPError]);

  const handleTextChange = (e: any) => {
    const value = e.target.value as string;
    setOtp(value);
    setOtpError(false);
  };

  const handleVerifyClick = () => {
    if (!otp.length) return;
    handleVerifyOtp(otp);
  };

  const handleResendClick = async () => {
    await handleResendOtp();
    setOtp('');
    setOtpError(false);
    setResendTimer(RESEND_OTP_SECONDS);
    setCanResend(false);
  };

  const handleKeyDown = (e: any) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleVerifyClick();
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      {loading ? (
        <Loader />
      ) : (
        <Grid
          container
          sx={{
            flexDirection: 'column',
          }}
        >
          <Grid
            container
            style={{ margin: '36px 0px', display: 'flex', cursor: 'pointer' }}
            onClick={() => setIsOTP(false)}
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid
              style={{
                height: '18px',
                width: '14px',
                marginRight: '12px',
              }}
            >
              <LeftArrow
                height="14px"
                width="14px"
                fill={patientAccessTheme.palette.text.mediumemphasis}
              />
            </Grid>

            <Typography
              textColor={patientAccessTheme.palette.text.mediumemphasis}
              fontSize="14px"
              lineHeight="23px"
              fontType="medium"
            >
              {constants.GOTO_LOGIN}
            </Typography>
          </Grid>
          <Grid style={{ marginBottom: '20px' }}>
            <img src={HOMA} alt="HOMA" />
          </Grid>
          {isOTPError && <InfoBar info={errorMessage} type="Error" />}
          <Grid style={{ marginBottom: '8px' }}>
            <Typography
              textColor={patientAccessTheme.palette.text.highemphasis}
              fontSize="24px"
              lineHeight="29px"
              fontType="semiBold"
            >
              {constants.OTP_VERIFICATION}
            </Typography>
          </Grid>
          <Grid style={{ marginBottom: '35px', maxWidth: '400px' }}>
            <Typography
              textColor={patientAccessTheme.palette.text.mediumemphasis}
              fontSize="14px"
              lineHeight="25px"
            >
              {constants.OTP_VERIFICATION_SUBTITLE +
                `${maskedEmail}` +
                '. Please enter the OTP below to verify.'}
            </Typography>
          </Grid>
          <Grid style={{ marginBottom: '48px' }}>
            <TextField
              width="372px"
              height="46px"
              placeholder={'Enter OTP'}
              onChange={(e: any) => handleTextChange(e)}
              value={otp}
              error={otpError}
              helperText={otpError ? errorMessage || 'Invalid OTP' : ''}
              autoFocus
              inputProps={{
                maxLength: 6,
              }}
            />
          </Grid>
          <Grid
            container
            sx={{
              flexDirection: 'column',
            }}
          >
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.status.white}
                  fontSize="16px"
                  lineHeight="24px"
                  fontType="semiBold"
                >
                  {'Verify OTP'}
                </Typography>
              }
              variant="contained"
              width="372px"
              height="44px"
              color="primary"
              borderRadius="4px"
              onClick={handleVerifyClick}
              disabled={!otp.length || otpError}
              styleProps={{ marginBottom: '1rem' }}
            />
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  fontSize="16px"
                  lineHeight="24px"
                  fontType="semiBold"
                >
                  {canResend ? 'Resend OTP' : `Resend OTP in ${resendTimer}s`}
                </Typography>
              }
              variant={canResend ? 'outlined' : 'text'}
              width="372px"
              height="44px"
              color="primary"
              borderRadius="4px"
              onClick={handleResendClick}
              disabled={!canResend || loading}
            />
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default LoginOTPOrganism;
